// Lifts a dropdown menu into the browser's top layer (Popover API) while it is
// open, so it escapes overflow-clipping ancestors such as scroll areas and
// modals instead of introducing its own scrollbar.
//
// The menu stays where it is in the DOM, so any inputs inside it keep
// belonging to their form and events keep bubbling to their usual ancestors.
// `position: fixed` alone is not enough here: the modal panel carries a
// transform, which would become the containing block of a fixed descendant.
//
// Browsers without the Popover API keep the menu's original absolute
// positioning untouched.

const GAP = 8;
const VIEWPORT_MARGIN = 8;

export const supportsPopover = () =>
  typeof HTMLElement !== "undefined" && Object.prototype.hasOwnProperty.call(HTMLElement.prototype, "popover");

export class FloatingMenu {
  // placement: "left" | "right" | "up_left" | "up_right"
  //   left/right is the edge of the anchor the menu aligns to; up_* opens above.
  // matchWidth: give the menu at least the anchor's width.
  constructor({ menu, anchor, placement = "left", matchWidth = false }) {
    this.menu = menu;
    this.anchor = anchor;
    this.placement = placement;
    this.matchWidth = matchWidth;
    this.enabled = supportsPopover();
    this.reposition = this.reposition.bind(this);

    if (this.enabled) this.prepare();
  }

  prepare() {
    // The user-agent popover styles (inset: 0, margin: auto, padding, border,
    // Canvas background...) would restyle the menu. Snapshot what the menu's own
    // classes produce before it becomes a popover and pin those values inline.
    const computed = window.getComputedStyle(this.menu);
    const preserved = {
      padding: computed.padding,
      borderRadius: computed.borderRadius,
      backgroundColor: computed.backgroundColor,
      color: computed.color,
      overflow: computed.overflow,
    };
    ["Top", "Right", "Bottom", "Left"].forEach(side => {
      ["Width", "Style", "Color"].forEach(prop => {
        const key = `border${side}${prop}`;
        preserved[key] = computed[key];
      });
    });

    this.menu.setAttribute("popover", "manual");

    Object.assign(this.menu.style, preserved, {
      position: "fixed",
      inset: "auto",
      margin: "0",
    });
  }

  // Call after the menu has been made visible (e.g. its `hidden` class removed).
  open() {
    if (!this.enabled) return;

    if (!this.isOpen()) this.menu.showPopover();
    this.clippingAncestors = findClippingAncestors(this.anchor);
    this.reposition();
    document.addEventListener("scroll", this.reposition, { capture: true, passive: true });
    window.addEventListener("resize", this.reposition, { passive: true });
    // The menu's own size can change while open (e.g. revealing more fields).
    if (typeof ResizeObserver !== "undefined") {
      // Deferred: reposition can set minWidth on the observed menu, which inside
      // the observer callback would trigger a "ResizeObserver loop" error.
      this.resizeObserver ||= new ResizeObserver(() => window.requestAnimationFrame(this.reposition));
      this.resizeObserver.observe(this.menu);
    }
  }

  // Call once the menu is hidden again (after any closing transition).
  close() {
    if (!this.enabled) return;

    this.stopListening();
    if (this.isOpen()) this.menu.hidePopover();
  }

  stopListening() {
    this.menu.style.visibility = "";
    document.removeEventListener("scroll", this.reposition, { capture: true });
    window.removeEventListener("resize", this.reposition);
    this.resizeObserver?.disconnect();
  }

  isOpen() {
    try {
      return this.menu.matches(":popover-open");
    } catch {
      return false;
    }
  }

  reposition() {
    if (!this.enabled || !this.isOpen()) return;

    const anchorRect = this.anchor.getBoundingClientRect();

    // While the trigger is scrolled out of its scroll area, hide the menu
    // rather than leaving it floating over unrelated content. It reappears if
    // the trigger is scrolled back into view.
    const anchorVisible = (anchorRect.width > 0 || anchorRect.height > 0) &&
      (this.clippingAncestors || []).every(el => intersects(anchorRect, el.getBoundingClientRect()));
    this.menu.style.visibility = anchorVisible ? "" : "hidden";

    if (this.matchWidth) this.menu.style.minWidth = `${anchorRect.width}px`;

    const menuWidth = this.menu.offsetWidth;
    const menuHeight = this.menu.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const alignRight = this.placement === "right" || this.placement === "up_right";
    let left = alignRight ? anchorRect.right - menuWidth : anchorRect.left;
    left = Math.min(left, viewportWidth - menuWidth - VIEWPORT_MARGIN);
    left = Math.max(left, VIEWPORT_MARGIN);

    const openUp = this.placement === "up_left" || this.placement === "up_right";
    const below = anchorRect.bottom + GAP;
    const above = anchorRect.top - GAP - menuHeight;
    let top = openUp ? above : below;
    // Flip when the preferred side has no room and the other side does.
    if (!openUp && below + menuHeight > viewportHeight && above >= 0) top = above;
    if (openUp && above < 0 && below + menuHeight <= viewportHeight) top = below;

    this.menu.style.left = `${Math.round(left)}px`;
    this.menu.style.top = `${Math.round(top)}px`;
  }

  disconnect() {
    this.stopListening();
  }
}

function intersects(a, b) {
  return a.bottom > b.top && a.top < b.bottom && a.right > b.left && a.left < b.right;
}

function findClippingAncestors(element) {
  const ancestors = [];
  let node = element.parentNode;
  while (node) {
    if (node instanceof ShadowRoot) {
      node = node.host;
      continue;
    }
    if (!(node instanceof Element) || node === document.documentElement) break;
    const { overflowX, overflowY } = window.getComputedStyle(node);
    if (overflowX !== "visible" || overflowY !== "visible") ancestors.push(node);
    node = node.parentNode;
  }
  return ancestors;
}
